import { DateTime } from "luxon";
import { z } from "zod";

export const REASON_CODES = [
  "CONTRACT_CHANGE",
  "APPROVED_EXCEPTION",
  "PRICING_UPDATE",
  "TEMPORARY_CHANGE",
  "CORRECTION",
  "PRODUCT_CHANGE",
  "OTHER",
] as const;

// Sentinel value used in the bundle dropdown to indicate the user wants to
// create a fresh single-use bundle from per-SKU prices instead of selecting
// an existing bundle. The backend swaps this for the real bundle id after
// creating the new PricingBundle row.
export const CUSTOM_PRICING_BUNDLE_ID = "__custom__";

const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;

const MIN_PRICE_IN_MILLICENTS = 250;

const priceInMillicentsSchema = z
  .number({
    required_error: "Required",
    invalid_type_error: "Required",
  })
  .int()
  .min(
    MIN_PRICE_IN_MILLICENTS,
    `Must be at least ${MIN_PRICE_IN_MILLICENTS} millicents`
  );

const customPriceSchema = z.object({
  skuId: z.string().min(1),
  priceInMillicents: priceInMillicentsSchema,
});

// Per-SKU prices arrive in two shapes:
//   1. From the frontend form, as a record `{ <skuId>: priceInMillicents }`
//      — React Hook Form addresses each input at `initialCustomPrices.<skuId>`,
//      so keeping this shape lets us emit per-SKU errors at that exact path.
//   2. From the backend mutation input, as an array of `{ skuId, priceInMillicents }`.
const customPriceListField = z
  .union([z.array(customPriceSchema), z.record(z.string(), z.unknown())])
  .nullish();

export type ReasonCodeValue = (typeof REASON_CODES)[number];

export const reasonCodeSchema = z.enum(REASON_CODES, {
  errorMap: (issue, ctx) => {
    if (
      issue.code === "invalid_type" &&
      (ctx.data === undefined || ctx.data === null)
    ) {
      return { message: "Required" };
    }
    return { message: `Invalid reason code: ${String(ctx.data)}` };
  },
});

// Accepts a JS Date or a valid Luxon DateTime; outputs a JS Date normalized
// to midnight UTC (any time component is stripped).
const flexDateSchema = z
  .union(
    [
      z.date({ errorMap: () => ({ message: "Required" }) }),
      z
        .custom<DateTime>(
          (val): val is DateTime => val instanceof DateTime && val.isValid
        )
        .transform(dt => dt.toJSDate()),
    ],
    {
      errorMap: () => ({ message: "Required" }),
    }
  )
  .transform(date => {
    const normalized = new Date(date.getTime());
    normalized.setUTCHours(0, 0, 0, 0);
    return normalized;
  });

const nullableFlexDateSchema = flexDateSchema.nullable();

// Preprocess a reason-code field: "" (MUI Select default) becomes null so that
// a union-abort on the empty string does not prevent superRefine from running.
const nullableReasonCodeField = z.preprocess(
  (val): unknown => (val === "" ? null : val),
  reasonCodeSchema.nullable()
);

const basePricingAssignmentFields = {
  initialBundleId: z.string().min(1, "Required"),
  // Use nullable so that an empty string (preprocessed to null) passes base
  // validation and does not abort superRefine.
  initialReasonCode: nullableReasonCodeField,
  initialNotes: z.string().nullish(),
  // Accept null so the form's default null value does not abort superRefine.
  effectiveDate: nullableFlexDateSchema,
  expirationDate: nullableFlexDateSchema.optional(),
  followUpBundleId: z.string().nullish(),
  followUpReasonCode: z.preprocess(
    (val): unknown => (val === "" ? null : val),
    reasonCodeSchema.nullable().optional()
  ),
  followUpNotes: z.string().nullish(),
  initialCustomPrices: customPriceListField,
  followUpCustomPrices: customPriceListField,
};

type CustomPriceValue = z.infer<typeof customPriceSchema>;
// Either of the two shapes produced by `customPriceListField`: a strict array
// (from the backend mutation) or a record (from the form). The record's value
// type is permissive — RhfCurrencyField may put `""` or other non-number
// transient values there while editing. `refinePricingAssignment` walks the
// record and emits per-SKU "Required" errors for any non-number entry.
type CustomPriceField =
  | CustomPriceValue[]
  | Record<string, unknown>
  | null
  | undefined;

function isCustomPricesEmpty(value: CustomPriceField): boolean {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return Object.keys(value).length === 0;
}

// Emit a per-SKU issue at `<basePath>.<skuId>` for every record entry whose
// value isn't a valid non-negative integer. The path matches the input's RHF
// field path so the error renders inline next to the offending SKU input.
function emitInvalidRecordEntryIssues(
  ctx: z.RefinementCtx,
  value: CustomPriceField,
  basePath: string
): void {
  if (value == null || Array.isArray(value)) {
    return;
  }
  for (const [skuId, entry] of Object.entries(value)) {
    if (typeof entry !== "number") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: [basePath, skuId],
      });
    } else if (entry < MIN_PRICE_IN_MILLICENTS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Must be at least ${MIN_PRICE_IN_MILLICENTS} millicents (${(
          MIN_PRICE_IN_MILLICENTS / 1000
        ).toFixed(2)} cents)`,
        path: [basePath, skuId],
      });
    } else if (!Number.isInteger(entry)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Must be a whole number of millicents",
        path: [basePath, skuId],
      });
    }
  }
}

type PricingAssignmentData = {
  initialBundleId: string;
  initialReasonCode: ReasonCodeValue | null;
  initialNotes?: string | null;
  effectiveDate: Date | null;
  expirationDate?: Date | null;
  followUpBundleId?: string | null;
  followUpReasonCode?: ReasonCodeValue | null;
  followUpNotes?: string | null;
  initialCustomPrices?: CustomPriceField;
  followUpCustomPrices?: CustomPriceField;
  customIsWeText?: boolean | null;
};

function refinePricingAssignment(
  data: PricingAssignmentData,
  ctx: z.RefinementCtx,
  {
    requireFollowUpBundle,
    requireCustomIsWeText,
  }: { requireFollowUpBundle: boolean; requireCustomIsWeText: boolean }
): void {
  if (!data.initialReasonCode) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["initialReasonCode"],
    });
  }
  if (!data.effectiveDate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["effectiveDate"],
    });
  }
  if (data.initialReasonCode === "OTHER" && !data.initialNotes?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Notes are required when the change reason is Other",
      path: ["initialNotes"],
    });
  }

  const initialIsCustom = data.initialBundleId === CUSTOM_PRICING_BUNDLE_ID;
  if (initialIsCustom) {
    if (isCustomPricesEmpty(data.initialCustomPrices)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["initialCustomPrices"],
      });
    } else {
      emitInvalidRecordEntryIssues(
        ctx,
        data.initialCustomPrices,
        "initialCustomPrices"
      );
    }
    if (
      requireCustomIsWeText &&
      (data.customIsWeText === null || data.customIsWeText === undefined)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["customIsWeText"],
      });
    }
  }

  if (data.expirationDate) {
    if (data.effectiveDate) {
      const oneDayAfter = new Date(
        data.effectiveDate.getTime() + ONE_DAY_IN_MS
      );
      if (data.expirationDate < oneDayAfter) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Expiration date must be at least one day after the effective date",
          path: ["expirationDate"],
        });
      }
    }
    // followUpReasonCode/followUpNotes without a followUpBundleId can occur when
    // the form held stale values that became hidden (e.g. followUpBundleId
    // switched to "None" after a reason was already chosen). Treat the bundle
    // as required whenever any follow-up field carries data.
    const hasFollowUpReasonOrNotes =
      !!data.followUpReasonCode || !!data.followUpNotes?.trim();
    if (
      (requireFollowUpBundle || hasFollowUpReasonOrNotes) &&
      !data.followUpBundleId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpBundleId"],
      });
    } else if (
      data.followUpBundleId &&
      data.followUpBundleId === data.initialBundleId &&
      data.followUpBundleId !== CUSTOM_PRICING_BUNDLE_ID
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "After-expiration bundle must be different from the primary bundle",
        path: ["followUpBundleId"],
      });
    }
    if (data.followUpBundleId === CUSTOM_PRICING_BUNDLE_ID) {
      if (isCustomPricesEmpty(data.followUpCustomPrices)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["followUpCustomPrices"],
        });
      } else {
        emitInvalidRecordEntryIssues(
          ctx,
          data.followUpCustomPrices,
          "followUpCustomPrices"
        );
      }
    }
    if (data.followUpBundleId && !data.followUpReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpReasonCode"],
      });
    } else if (requireFollowUpBundle && !data.followUpReasonCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["followUpReasonCode"],
      });
    }
    if (data.followUpReasonCode === "OTHER" && !data.followUpNotes?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Notes are required when the change reason is Other",
        path: ["followUpNotes"],
      });
    }
  }
}

export const changeTenantPricingAssignmentSchema = z
  .object({
    ...basePricingAssignmentFields,
    customIsWeText: z.boolean().nullish(),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: true,
      requireCustomIsWeText: true,
    });
  });

// Subaccount assignments allow gaps, so the after-expiration bundle is
// optional; the rest of the validation mirrors the tenant schema. Subaccount
// mode does not need customIsWeText — isWeText is derived from the selected
// subaccounts (already locked by the form's weTextLock).
export const createSubaccountPricingAssignmentsSchema = z
  .object({
    ...basePricingAssignmentFields,
    subaccountIds: z.array(z.string().min(1)).min(1, "Required"),
  })
  .superRefine((data, ctx) => {
    refinePricingAssignment(data, ctx, {
      requireFollowUpBundle: false,
      requireCustomIsWeText: false,
    });
  });
